import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';

interface AccountCount {
  account_id: string;
  account_name: string;
  provider: 'aws' | 'gcp';
  count: number;
  error: string | null;
}

interface InstanceCountsResponse {
  total: number;
  scanned_at: string;
  scan_duration_ms: number;
  accounts: AccountCount[];
  cached: boolean;
  cache_age_seconds: number;
}

export default function CloudInstanceCounter() {
  const [data, setData] = useState<InstanceCountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const lastFetchTime = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCounts = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const resp = await axios.get<InstanceCountsResponse>(
        `${getApiUrl()}/admin/cloud-instance-counts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(resp.data);
      setError(null);
      lastFetchTime.current = Date.now();
      setSecondsAgo(0);
    } catch (err: any) {
      if (err.response?.status === 401) return;
      setError(err.response?.status === 502 ? 'Cloud scan unavailable' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();

    // Poll every 60s, pause when hidden
    const start = () => {
      intervalRef.current = setInterval(fetchCounts, 60_000);
    };
    const stop = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        fetchCounts();
        start();
      }
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);

    // Tick secondsAgo counter every second
    tickRef.current = setInterval(() => {
      if (lastFetchTime.current) {
        setSecondsAgo(Math.floor((Date.now() - lastFetchTime.current) / 1000));
      }
    }, 1000);

    return () => {
      stop();
      if (tickRef.current) clearInterval(tickRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchCounts]);

  // Loading skeleton
  if (loading && !data) {
    return (
      <div className="mb-6 bg-gray-900 border border-gray-800 rounded-xl p-5 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-3 w-28 bg-gray-700 rounded" />
          <div className="h-3 w-24 bg-gray-700 rounded" />
        </div>
        <div className="h-10 w-16 bg-gray-700 rounded mx-auto mb-2" />
        <div className="h-3 w-40 bg-gray-700 rounded mx-auto mb-4" />
        <div className="flex gap-2 flex-wrap justify-center">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-6 w-24 bg-gray-700 rounded-full" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="mb-6 bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Live Instances</span>
          <button
            onClick={fetchCounts}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            Retry
          </button>
        </div>
        <p className="text-center text-gray-500 text-sm mt-3">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const allClear = data.total === 0;
  const hasErrors = data.accounts.some(a => a.error);
  const ageLabel = secondsAgo < 5 ? 'just now' : `${secondsAgo}s ago`;

  return (
    <div className={`mb-6 rounded-xl p-5 border transition-colors ${
      allClear
        ? 'bg-emerald-950/30 border-emerald-800/40'
        : 'bg-gray-900 border-gray-800'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
          Live Instances
        </span>
        <div className="flex items-center gap-2">
          {data.cached && (
            <span className="text-[10px] text-gray-600 uppercase">cached</span>
          )}
          <button
            onClick={fetchCounts}
            className="text-gray-500 hover:text-gray-300 transition-colors"
            title="Refresh"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <span className="text-[10px] text-gray-600">
            scanned {ageLabel}
          </span>
        </div>
      </div>

      {/* Total */}
      {allClear ? (
        <p className="text-center text-emerald-400 text-sm font-medium py-2">
          All clear — no instances running
        </p>
      ) : (
        <div className="text-center mb-3">
          <span className="text-4xl font-bold text-white tabular-nums">{data.total}</span>
          <p className="text-xs text-gray-500 mt-0.5">running across all clouds</p>
        </div>
      )}

      {/* Per-account badges grouped by CSP */}
      {(() => {
        const awsAccounts = data.accounts.filter(a => a.provider === 'aws');
        const gcpAccounts = data.accounts.filter(a => a.provider === 'gcp');

        const Badge = ({ acct }: { acct: AccountCount }) => {
          const hasError = !!acct.error;
          const isGcp = acct.provider === 'gcp';
          return (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                hasError
                  ? 'bg-amber-500/10 text-amber-400 border border-amber-700/30'
                  : isGcp
                  ? 'bg-teal-500/10 text-teal-400'
                  : 'bg-slate-500/10 text-slate-400'
              }`}
              title={hasError ? `Error: ${acct.error}` : `${acct.count} instance${acct.count !== 1 ? 's' : ''}`}
            >
              {hasError && (
                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {acct.account_name} {hasError ? '!' : acct.count}
            </span>
          );
        };

        return (
          <div className="flex flex-col gap-1.5 items-center mt-2">
            {awsAccounts.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap justify-center">
                <span className="text-[10px] text-gray-600 uppercase font-medium mr-0.5">AWS</span>
                {awsAccounts.map(acct => <Badge key={acct.account_id} acct={acct} />)}
              </div>
            )}
            {gcpAccounts.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap justify-center">
                <span className="text-[10px] text-gray-600 uppercase font-medium mr-0.5">GCP</span>
                {gcpAccounts.map(acct => <Badge key={acct.account_id} acct={acct} />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* Scan duration in corner */}
      {hasErrors && (
        <p className="text-[10px] text-amber-600 text-center mt-2">
          Some accounts could not be scanned — hover badges for details
        </p>
      )}
    </div>
  );
}
